import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import type { Capabilities } from "../api/capabilities.js";
import { useApiClient } from "../api/ApiClientContext.js";
import { getItem } from "../api/items.js";
import { Dialog } from "../components/Dialog.js";
import { asItemId } from "../domain/ids.js";
import { ItemForm } from "./ItemForm.js";

/**
 * Routed wrapper: resolves `mode`/`:id` into the data `ItemForm` needs, and
 * owns loading/error states for edit mode. Rendered from `ItemListPage`'s
 * `<Outlet/>`, so the form (and these interim states) always float as a modal
 * over the list — the successor to legacy's `AddItemWindow.ShowDialog()`.
 */
export function ItemFormPage({ mode, capabilities }: { mode: "create" | "edit"; capabilities: Capabilities }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const client = useApiClient();

  const itemQuery = useQuery({
    queryKey: ["item", id],
    queryFn: () => getItem(client, asItemId(id ?? "")),
    enabled: mode === "edit" && Boolean(id),
  });

  function goToList() {
    navigate("/");
  }

  if (mode === "create") {
    return <ItemForm mode="create" item={null} capabilities={capabilities} onDone={goToList} />;
  }

  if (!id) return <Navigate to="/" replace />;

  if (itemQuery.isPending) {
    return (
      <Dialog title="Nalaganje" onClose={goToList}>
        <p role="status">Nalaganje …</p>
      </Dialog>
    );
  }

  if (itemQuery.isError) {
    return (
      <Dialog title="Napaka" onClose={goToList}>
        <p role="alert">Vnosa ni bilo mogoče naložiti.</p>
      </Dialog>
    );
  }

  return <ItemForm mode="edit" item={itemQuery.data.item} capabilities={capabilities} onDone={goToList} />;
}
